import React, { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import {
  Stack,
  Typography,
  Box,
  Chip,
  CircularProgress,
  Button,
  IconButton,
  Menu,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  ListItemIcon,
  useTheme,
} from '@mui/material';
import {
  Add,
  MoreVert as MoreVertIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import { BorderedBlock } from '@shared/ui/common/BorderedBlock';
import { useSuppliers } from '@features/suppliers/hooks/useSuppliers';
import { useSupplier } from '@features/suppliers/hooks/useSupplier';
import { useNavigate } from 'react-router-dom';
import { useBottomSheet, BottomSheetSize } from '@shared/shell/bottomsheet/useBottomSheet';
import { AddSupplierToProductSheet } from '@features/products';
import SupplierSelectBottomSheet from '@features/suppliers/components/links/SupplierSelectBottomSheet';
import { Supplier, SupplierProduct } from '@domain/suppliers/models/Supplier';
import { Product } from '@domain/product/models/Product';
import { useToast } from '@shared/hooks/useToast';

interface ProductSuppliersBlockProps {
  product: Product;
}

const formatPrice = (price: number, currency?: string) => {
  if (currency) {
    return new Intl.NumberFormat(undefined, { style: 'currency', currency }).format(price);
  }
  return price.toLocaleString();
};

const formatMoq = (link: SupplierProduct): string => {
  if (link.minimumOrderQuantity == null) {
    return '—';
  }
  if (link.minimumOrderQuantityType === 'currency') {
    return formatPrice(link.minimumOrderQuantity, link.currency);
  }
  return `${link.minimumOrderQuantity} items`;
};

const formatMultiplesOf = (link: SupplierProduct): string => {
  if (link.orderInMultiplesOf == null) {
    return '—';
  }
  return String(link.orderInMultiplesOf);
};

const ProductSuppliersBlock: React.FC<ProductSuppliersBlockProps> = ({ product }) => {
  const theme = useTheme();
  const queryClient = useQueryClient();
  const { success, error: showError } = useToast();
  const navigate = useNavigate();
  const { openBottomSheet, closeBottomSheet } = useBottomSheet();
  const { suppliers, isLoading } = useSuppliers();
  const { unlinkProduct, isUnlinkingProduct } = useSupplier('');

  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);
  const [menuRow, setMenuRow] = useState<{ supplier: Supplier; link: SupplierProduct } | null>(null);
  const [unlinkTarget, setUnlinkTarget] = useState<{ supplier: Supplier; link: SupplierProduct } | null>(null);

  const productSuppliers = React.useMemo(() => {
    return suppliers
      .map((supplier: Supplier) => {
        const link = supplier.supplierProducts?.find(
          (lp: SupplierProduct) => lp.product?.id === product.id
        );
        return link ? { supplier, link } : null;
      })
      .filter(Boolean) as { supplier: Supplier; link: SupplierProduct }[];
  }, [suppliers, product.id]);

  const handleSupplierClick = (supplierId: string) => {
    navigate(`/suppliers/${supplierId}`);
  };

  const handleAddSupplier = () => {
    openBottomSheet({
      title: 'Select supplier',
      component: (
        <SupplierSelectBottomSheet
          linkedToProductId={product.id}
          onSelect={s => {
            closeBottomSheet();
            openBottomSheet({
              title: 'Add supplier to product',
              component: (
                <AddSupplierToProductSheet product={product} supplier={s} onClose={closeBottomSheet} />
              ),
              size: BottomSheetSize.MD,
            });
          }}
          onClose={closeBottomSheet}
        />
      ),
      size: BottomSheetSize.MD,
    });
  };

  const openRowMenu = (event: React.MouseEvent<HTMLElement>, row: { supplier: Supplier; link: SupplierProduct }) => {
    event.stopPropagation();
    setMenuAnchor(event.currentTarget);
    setMenuRow(row);
  };

  const closeRowMenu = () => {
    setMenuAnchor(null);
    setMenuRow(null);
  };

  const handleEditLink = () => {
    if (!menuRow) return;
    const { supplier, link } = menuRow;
    closeRowMenu();
    openBottomSheet({
      title: 'Edit supplier link',
      component: (
        <AddSupplierToProductSheet
          product={product}
          supplier={supplier}
          existingLink={link}
          onClose={closeBottomSheet}
        />
      ),
      size: BottomSheetSize.MD,
    });
  };

  const openUnlinkDialog = () => {
    if (menuRow) {
      setUnlinkTarget(menuRow);
    }
    closeRowMenu();
  };

  const handleConfirmUnlink = async () => {
    if (!unlinkTarget || !unlinkTarget.link.product) return;
    const supplierName = unlinkTarget.supplier.name;

    try {
      await unlinkProduct({
        supplierId: unlinkTarget.supplier.id,
        productId: unlinkTarget.link.product.id,
      });
      await queryClient.invalidateQueries({ queryKey: ['product', product.id] });
      setUnlinkTarget(null);
      success(`Unlinked ${supplierName}`);
    } catch (e) {
      showError(e instanceof Error ? e.message : 'Failed to unlink supplier');
    }
  };

  return (
    <BorderedBlock
      sx={{ p: 0 }}
      header={
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Typography variant="subtitle2">Suppliers</Typography>
          <Button endIcon={<Add />} variant="text" color="primary" onClick={handleAddSupplier}>
            Add supplier
          </Button>
        </Stack>
      }
    >
      {isLoading ? (
        <Stack alignItems="center" py={4}>
          <CircularProgress size={24} />
        </Stack>
      ) : productSuppliers.length === 0 ? (
        <Box sx={{ p: 4 }}>
          <Typography variant="subtitle2" color="text.secondary">
            No suppliers linked yet. Add a supplier or link products from a supplier page.
          </Typography>
        </Box>
      ) : (
        <TableContainer sx={{ overflowX: 'auto' }}>
          <Table size="small" sx={{ minWidth: 720 }}>
            <TableHead>
              <TableRow sx={{ bgcolor: theme.palette.background.default }}>
                <TableCell sx={{ py: 1.5 }}>Supplier</TableCell>

                <TableCell align="right" sx={{ py: 1.5, minWidth: 120 }}>
                  MOQ
                </TableCell>
                <TableCell align="right" sx={{ py: 1.5, minWidth: 100 }}>
                  In multiples of
                </TableCell>
                <TableCell align="right" sx={{ py: 1.5 }}>
                  Purchase price
                </TableCell>
                <TableCell align="right" sx={{ py: 1.5, width: 56, pr: 1 }}>

                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {productSuppliers.map(({ supplier, link }) => (
                <TableRow
                  key={supplier.id}
                  hover
                  sx={{ cursor: 'pointer' }}
                  onClick={() => handleSupplierClick(supplier.id)}
                >
                  <TableCell sx={{ p: 2 }}>
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <Typography variant="body2">{supplier.name}</Typography>
                      {link.isMain ? (
                        <Chip label="Main" size="small" color="primary" />
                      ) : (
                        <Typography variant="body2" color="text.secondary" component="span">
                          —
                        </Typography>
                      )}
                    </Stack>
                  </TableCell>

                  <TableCell align="right" sx={{ py: 1.5 }}>
                    <Typography variant="body2" color="text.secondary">
                      {formatMoq(link)}
                    </Typography>
                  </TableCell>
                  <TableCell align="right" sx={{ py: 1.5 }}>
                    <Typography variant="body2" color="text.secondary">
                      {formatMultiplesOf(link)}
                    </Typography>
                  </TableCell>
                  <TableCell align="right" sx={{ py: 1.5 }}>
                    <Typography variant="body2" color="text.secondary">
                      {link.purchasePrice != null ? formatPrice(link.purchasePrice, link.currency) : '—'}
                    </Typography>
                  </TableCell>
                  <TableCell
                    align="right"
                    sx={{ py: 0.5, pr: 1 }}
                    onClick={e => e.stopPropagation()}
                  >
                    <IconButton
                      size="small"
                      aria-label={`Actions for ${supplier.name}`}
                      onClick={e => openRowMenu(e, { supplier, link })}
                    >
                      <MoreVertIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Menu anchorEl={menuAnchor} open={Boolean(menuAnchor)} onClose={closeRowMenu}>
        <MenuItem
          onClick={() => {
            handleEditLink();
          }}
        >
          <ListItemIcon>
            <EditIcon fontSize="small" />
          </ListItemIcon>
          Edit link
        </MenuItem>
        <MenuItem
          onClick={openUnlinkDialog}
          sx={{ color: 'error.main' }}
        >
          <ListItemIcon>
            <DeleteIcon fontSize="small" color="error" />
          </ListItemIcon>
          Unlink supplier
        </MenuItem>
      </Menu>

      <Dialog
        open={Boolean(unlinkTarget)}
        onClose={() => !isUnlinkingProduct && setUnlinkTarget(null)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Unlink supplier</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to unlink &quot;{unlinkTarget?.supplier.name}&quot; from this product? This
            action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setUnlinkTarget(null)} disabled={isUnlinkingProduct}>
            Cancel
          </Button>
          <Button
            onClick={() => void handleConfirmUnlink()}
            color="error"
            variant="contained"
            disabled={isUnlinkingProduct}
          >
            {isUnlinkingProduct ? 'Unlinking…' : 'Unlink'}
          </Button>
        </DialogActions>
      </Dialog>
    </BorderedBlock>
  );
};

export default ProductSuppliersBlock;
